import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  AlertTriangle,
  Package,
  TrendingDown,
  Activity,
  AlertCircle
} from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { InitialScreen } from '@/pages/ItemsContainer/ItemsWantedReport/components/IntialScreen';
import LoadingBox from '@/UI/LoadingBox';

import type { IItemCheckerReport } from './api/ItemChecker.interface';
import ReportHeader from './components/FormHeader';
import ItemTable from './components/ItemsTable';

function ItemsWantedReport() {
  const [loading, setLoading] = useState<boolean>(false);
  const [allData, setAllData] = useState<IItemCheckerReport[] | null>(null);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!allData || allData.length === 0) return null;
    
    const criticalItems = allData.filter(item => 
      item.retailStatus === 'needStock' || item.wholesaleStatus === 'needStock'
    ).length;
    const warningItems = allData.filter(item => 
      item.retailStatus === 'canTransfer' || item.wholesaleStatus === 'canTransfer'
    ).length;
    const overStockItems = allData.filter(item => 
      item.retailStatus === 'overStock' || item.wholesaleStatus === 'overStock'
    ).length;
    const totalRetailBalance = allData.reduce((sum, item) => sum + item.retailBalance, 0);
    const totalWholesaleBalance = allData.reduce((sum, item) => sum + item.wholesaleBalance, 0);
    
    return {
      totalItems: allData.length,
      criticalItems,
      warningItems,
      overStockItems,
      totalRetailBalance: Math.round(totalRetailBalance),
      totalWholesaleBalance: Math.round(totalWholesaleBalance),
      criticalPercentage: Math.round((criticalItems / allData.length) * 100)
    };
  }, [allData]);

  return (
    <>
      <Helmet>
        <title>تقرير نواقص الأصناف</title>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/5">
        {/* Background Pattern */}
        <div className="fixed inset-0 opacity-[0.015] pointer-events-none">
          <div className="absolute inset-0 bg-grid-black/[0.02] dark:bg-grid-white/[0.02]" />
        </div>
        
        <div className="relative flex w-full flex-col gap-4 p-4">
          {/* Enhanced Header */}
          <Card className={cn(
            "relative overflow-hidden",
            "bg-gradient-to-r from-red-50/90 via-orange-50/90 to-amber-50/90",
            "dark:from-red-950/40 dark:via-orange-950/40 dark:to-amber-950/40",
            "border-0 shadow-xl"
          )}>
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-red-500/10 to-transparent rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-orange-500/10 to-transparent rounded-full blur-3xl" />
            
            <div className="relative p-6">
              <ReportHeader
                loading={loading}
                setAllData={setAllData}
                setLoading={setLoading}
              />
              
              {/* Summary Statistics */}
              {summaryStats && !loading && (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {/* Total Items */}
                  <Card className={cn(
                    "p-4 border-0",
                    "bg-gradient-to-br from-blue-50 to-blue-100/50",
                    "dark:from-blue-950/30 dark:to-blue-900/20",
                    "shadow-lg"
                  )}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">إجمالي الأصناف</p>
                        <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                          {summaryStats.totalItems}
                        </p>
                      </div>
                      <Package className="h-8 w-8 text-blue-500 opacity-50" />
                    </div>
                  </Card>
                  
                  {/* Critical Items */}
                  <Card className={cn(
                    "p-4 border-0",
                    "bg-gradient-to-br from-red-50 to-red-100/50",
                    "dark:from-red-950/30 dark:to-red-900/20",
                    "shadow-lg animate-pulse"
                  )}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">أصناف حرجة</p>
                        <p className="text-2xl font-bold text-red-700 dark:text-red-400">
                          {summaryStats.criticalItems}
                        </p>
                      </div>
                      <AlertTriangle className="h-8 w-8 text-red-500" />
                    </div>
                  </Card>
                  
                  {/* Warning Items */}
                  <Card className={cn(
                    "p-4 border-0",
                    "bg-gradient-to-br from-amber-50 to-amber-100/50",
                    "dark:from-amber-950/30 dark:to-amber-900/20",
                    "shadow-lg"
                  )}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">تحتاج متابعة</p>
                        <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">
                          {summaryStats.warningItems}
                        </p>
                      </div>
                      <AlertCircle className="h-8 w-8 text-amber-500 opacity-70" />
                    </div>
                  </Card>
                  
                  {/* Retail Balance */}
                  <Card className={cn(
                    "p-4 border-0",
                    "bg-gradient-to-br from-purple-50 to-purple-100/50",
                    "dark:from-purple-950/30 dark:to-purple-900/20",
                    "shadow-lg"
                  )}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">رصيد القطاعي</p>
                        <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                          {summaryStats.totalRetailBalance.toLocaleString()}
                        </p>
                      </div>
                      <TrendingDown className="h-8 w-8 text-purple-500 opacity-50" />
                    </div>
                  </Card>
                  
                  {/* Critical Percentage */}
                  <Card className={cn(
                    "p-4 border-0",
                    "bg-gradient-to-br from-emerald-50 to-emerald-100/50",
                    "dark:from-emerald-950/30 dark:to-emerald-900/20",
                    "shadow-lg"
                  )}>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">نسبة الحرج</p>
                        <Activity className="h-5 w-5 text-emerald-500 opacity-50" />
                      </div>
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                            {summaryStats.criticalPercentage}%
                          </span>
                        </div>
                        <Progress 
                          value={summaryStats.criticalPercentage} 
                          className="h-1.5 mt-2 bg-emerald-100 dark:bg-emerald-950/30"
                        >
                          <div 
                            className={cn(
                              "h-full rounded-full transition-all duration-500",
                              summaryStats.criticalPercentage > 50 
                                ? "bg-gradient-to-r from-red-500 to-red-600"
                                : summaryStats.criticalPercentage > 25
                                  ? "bg-gradient-to-r from-amber-500 to-amber-600"
                                  : "bg-gradient-to-r from-emerald-500 to-emerald-600"
                            )}
                            style={{ width: `${summaryStats.criticalPercentage}%` }}
                          />
                        </Progress>
                      </div>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          </Card>

          {/* Main Content Area */}
          <div className="relative">
            {allData && (
              <Card className={cn(
                "overflow-hidden border-0",
                "bg-gradient-to-br from-background/95 via-background to-muted/5",
                "dark:from-slate-900/95 dark:via-slate-900 dark:to-slate-800/20",
                "shadow-xl backdrop-blur-sm"
              )}>
                <div className="relative">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-2xl" />
                  <ItemTable items={allData} />
                </div>
              </Card>
            )}
            
            {!allData && !loading && (
              <Card className={cn(
                "overflow-hidden border-0",
                "bg-gradient-to-br from-background/95 via-background to-muted/5",
                "dark:from-slate-900/95 dark:via-slate-900 dark:to-slate-800/20",
                "shadow-xl"
              )}>
                <InitialScreen />
              </Card>
            )}
            
            {loading && (
              <Card className={cn(
                "overflow-hidden border-0",
                "bg-gradient-to-br from-background/95 via-background to-muted/5",
                "dark:from-slate-900/95 dark:via-slate-900 dark:to-slate-800/20",
                "shadow-xl"
              )}>
                <div className="flex h-[70vh] flex-col items-center justify-center p-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/5 blur-3xl animate-pulse" />
                    <LoadingBox />
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground animate-pulse">
                    جاري تحليل البيانات...
                  </p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ItemsWantedReport;
